import type {
  AnnotatedPdfShape,
  AnnotatedShape,
  Annotation,
  AnnotationShape,
  ImageShape,
  VideoShape,
} from './annotation';

export function parseAnnotation(raw: unknown): Annotation | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  if (obj.kind === 'image') {
    const shapes = parseAnnotatedShapes(obj.shapes, isImageShape);
    return shapes ? { kind: 'image', shapes } : null;
  }
  if (obj.kind === 'video' && typeof obj.timestamp === 'number') {
    const shapes =
      obj.shapes === undefined || (Array.isArray(obj.shapes) && obj.shapes.length === 0)
        ? []
        : parseAnnotatedShapes(obj.shapes, isVideoShape);
    return shapes ? { kind: 'video', timestamp: obj.timestamp, shapes } : null;
  }
  if (obj.kind === 'pdf') {
    // New format: each shape carries `page`. Old format (pre-multi-page):
    // top-level `page` applied uniformly. Both are handled so persisted
    // annotations from the early branch testing continue to load.
    const shapes = parsePdfShapes(obj.shapes, typeof obj.page === 'number' ? obj.page : null);
    return shapes ? { kind: 'pdf', shapes } : null;
  }
  if (
    obj.kind === 'text' &&
    typeof obj.sectionId === 'string' &&
    typeof obj.from === 'number' &&
    typeof obj.to === 'number'
  ) {
    return {
      kind: 'text',
      sectionId: obj.sectionId,
      from: obj.from,
      to: obj.to,
      ...(obj.detached === true ? { detached: true } : {}),
    };
  }
  if (obj.kind === 'field' && typeof obj.sectionId === 'string') {
    return { kind: 'field', sectionId: obj.sectionId };
  }
  return null;
}

function parsePdfShapes(raw: unknown, fallbackPage: number | null): AnnotatedPdfShape[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: AnnotatedPdfShape[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return null;
    const e = entry as Record<string, unknown>;
    const shape = parseShape(e.shape);
    if (!shape || !isAnyShape(shape)) return null;
    if (typeof e.color !== 'string') return null;
    const page = typeof e.page === 'number' ? e.page : fallbackPage;
    if (page === null) return null;
    out.push({ shape, color: e.color, page });
  }
  return out;
}

function parseAnnotatedShapes<S extends AnnotationShape>(
  raw: unknown,
  isAllowed: (s: AnnotationShape) => s is S,
): AnnotatedShape<S>[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: AnnotatedShape<S>[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return null;
    const e = entry as Record<string, unknown>;
    const shape = parseShape(e.shape);
    if (!shape || !isAllowed(shape)) return null;
    if (typeof e.color !== 'string') return null;
    out.push({ shape, color: e.color });
  }
  return out;
}

function isImageShape(s: AnnotationShape): s is ImageShape {
  return s.type !== 'text-highlight';
}

function isVideoShape(s: AnnotationShape): s is VideoShape {
  return s.type !== 'text-highlight';
}

function isAnyShape(s: AnnotationShape): s is AnnotationShape {
  return !!s;
}

function parseShape(raw: unknown): AnnotationShape | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  switch (obj.type) {
    case 'pen':
      return isPointArray(obj.points) ? { type: 'pen', points: obj.points } : null;
    case 'circle':
      return isRect(obj.rect) ? { type: 'circle', rect: obj.rect } : null;
    case 'rectangle':
      return isRect(obj.rect) ? { type: 'rectangle', rect: obj.rect } : null;
    case 'arrow':
      return isPoint(obj.start) && isPoint(obj.end)
        ? { type: 'arrow', start: obj.start, end: obj.end }
        : null;
    case 'text-highlight':
      return isRectArray(obj.rects) && typeof obj.text === 'string'
        ? { type: 'text-highlight', rects: obj.rects, text: obj.text }
        : null;
    default:
      return null;
  }
}

function isPoint(v: unknown): v is { x: number; y: number } {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as { x: unknown }).x === 'number' &&
    typeof (v as { y: unknown }).y === 'number'
  );
}

function isPointArray(v: unknown): v is { x: number; y: number }[] {
  return Array.isArray(v) && v.every(isPoint);
}

function isRect(v: unknown): v is { x: number; y: number; w: number; h: number } {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.x === 'number' &&
    typeof r.y === 'number' &&
    typeof r.w === 'number' &&
    typeof r.h === 'number'
  );
}

function isRectArray(v: unknown): v is { x: number; y: number; w: number; h: number }[] {
  return Array.isArray(v) && v.every(isRect);
}
